import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { auth, api, fmtINR } from "@/lib/api";
import { useTickConnected } from "@/lib/ticks";
import { useInterval } from "@/lib/useInterval";
import {
  LayoutDashboard, ListOrdered, BookOpen, Wallet, Activity, Megaphone,
  Users, Ban, Calendar, LineChart, LogOut, Radio, Zap, Trophy, BarChart3, ScrollText,
  ShieldAlert, Sun, Moon, Settings, CalendarCheck, Layers, Trash2, Gauge
} from "lucide-react";
import BroadcastBar from "./BroadcastBar";
import MarketClosedBanner from "./MarketClosedBanner";
import { useTheme } from "@/lib/theme";

const clientNav = [
  { to: "/dashboard",  label: "Market Watch", icon: LayoutDashboard },
  { to: "/orders",     label: "Pending Orders", icon: ListOrdered },
  { to: "/tradebook",  label: "Trade Book",   icon: BookOpen },
  { to: "/positions",  label: "Net Position", icon: Activity },
  { to: "/ledger",     label: "Ledger",       icon: Wallet },
];

const adminNav = [
  { to: "/admin",                 label: "Overview",       icon: LayoutDashboard },
  { to: "/admin/marketwatch",     label: "Market Watch",   icon: LineChart },
  { to: "/admin/clients",         label: "Clients",        icon: Users },
  { to: "/admin/pending",         label: "Pending Orders", icon: ListOrdered },
  { to: "/admin/trades",          label: "Trade Book",     icon: BookOpen },
  { to: "/admin/positions",       label: "Positions",      icon: Activity },
  { to: "/admin/leaderboard",     label: "Leaderboard",    icon: Trophy },
  { to: "/admin/vwap",            label: "VWAP Stats",     icon: BarChart3 },
  { to: "/admin/broadcast",       label: "Broadcasts",     icon: Megaphone },
  { to: "/admin/bans",            label: "Banned Stocks",  icon: Ban },
  { to: "/admin/bulk",            label: "Bulk Ops",       icon: Layers },
  { to: "/admin/closing-prices",  label: "Closing Prices", icon: Calendar },
  { to: "/admin/closing-prices-audit", label: "Closing Price Audit", icon: ScrollText },
  { to: "/admin/market-calendar", label: "Market Calendar",icon: Calendar },
  { to: "/admin/uc-lc",           label: "UC/LC Audit",    icon: Gauge },
  { to: "/admin/settlements",     label: "Settlements",    icon: CalendarCheck },
  { to: "/admin/audit",           label: "Audit Log",      icon: ScrollText },
  { to: "/admin/system",          label: "System",         icon: Settings },
  { to: "/admin/data-purge",      label: "Data Purge",     icon: Trash2 },
  { to: "/admin/activity-logs",   label: "Activity Log",   icon: ScrollText },
];

export default function Layout() {
  const user = auth.getUser();
  const nav = useNavigate();
  const { connected } = { connected: useTickConnected() };
  const [summary, setSummary] = useState(null);
  const [feed, setFeed] = useState(null);
  const [sysSettings, setSysSettings] = useState(null);
  const { theme, toggle: toggleTheme } = useTheme();

  const items = user?.role === "admin" ? adminNav : clientNav;

  useEffect(() => {
    // One-time feed status on mount (rarely changes; pulled live via WS too)
    api.get("/feed/status").then((r) => setFeed(r.data)).catch(() => {});
  }, []);

  // System settings: changes infrequently. WS also pushes read_only events.
  // Visibility-aware: pauses when tab hidden, resumes on focus.
  useInterval(() => {
    api.get("/system/settings").then((r) => setSysSettings(r.data)).catch(() => {});
  }, 20000);

  // Per-client summary: only for clients, slower interval (WS pushes order/trade events).
  useInterval(() => {
    if (user?.role !== "client") return;
    api.get("/summary/me").then((r) => setSummary(r.data)).catch(() => {});
  }, user?.role === "client" ? 8000 : 0);

  const logout = () => {
    auth.clear();
    nav("/login", { replace: true });
  };

  return (
    <div className={`min-h-screen bg-secondary/40 ${sysSettings?.read_only_mode ? "pt-[3.75rem]" : "pt-8"}`}>
      <BroadcastBar />

      {sysSettings?.read_only_mode && (
        <div className="fixed top-8 left-0 right-0 z-40 h-7 bg-rose-100 text-destructive text-xs font-bold flex items-center justify-center gap-2" data-testid="read-only-banner">
          <ShieldAlert className="h-3 w-3" /> SYSTEM IS IN READ-ONLY MODE — trading is temporarily disabled by admin.
        </div>
      )}

      <MarketClosedBanner />

      <header className="sticky top-0 z-40 bg-card border-b border-border" data-testid="topbar">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-14 flex items-center gap-4">
          <Link to={user?.role === "admin" ? "/admin" : "/dashboard"} className="flex items-center gap-2">
            <img src="/logo192.png" alt="ADITI5" className="brand-logo h-9 w-9 rounded-lg" data-testid="brand-logo" />
            <div className="leading-none">
              <div className="text-sm font-bold tracking-tight">ADITI5</div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                {user?.role === "admin" ? "Admin Console" : "Client Terminal"}
              </div>
            </div>
          </Link>

          <div className="ml-4 hidden md:flex items-center gap-2 text-xs">
            <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-500 pulse-dot" : "bg-slate-300"}`} />
            <span className="text-muted-foreground">{connected ? "LIVE" : "Connecting…"}</span>
          </div>

          <div className="ml-auto flex items-center gap-3">
            {user?.role === "client" && summary && (
              <div className="hidden md:flex items-center gap-4 text-xs">
                <Stat label="Balance" value={fmtINR(summary.balance)} />
                <Stat label="MTM" value={fmtINR(summary.unrealised_mtm)} colored={summary.unrealised_mtm} />
                <Stat label="Margin Used" value={fmtINR(summary.margin_used)} />
              </div>
            )}
            <div className="text-right">
              <div className="text-sm font-semibold leading-none">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {user?.role === "client" ? user.client_id : user?.email}
              </div>
            </div>
            <button onClick={toggleTheme} data-testid="theme-toggle"
                    className="h-9 w-9 grid place-items-center rounded-md hover:bg-secondary transition-colors"
                    title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button onClick={logout} data-testid="logout-btn"
                    className="h-9 w-9 grid place-items-center rounded-md hover:bg-secondary transition-colors"
                    title="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 grid grid-cols-12 gap-4">
        <aside className="col-span-12 md:col-span-2 lg:col-span-2">
          <nav className="sticky top-14 flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible bg-card rounded-xl border border-border p-2" data-testid="sidebar-nav">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end
                       data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                       className={({ isActive }) =>
                         `flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors
                          ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-foreground"}`
                       }>
                <it.icon className="h-4 w-4" />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="col-span-12 md:col-span-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Stat({ label, value, colored }) {
  let cls = "font-semibold num";
  if (colored !== undefined) {
    cls += colored > 0 ? " text-profit" : colored < 0 ? " text-loss" : "";
  }
  return (
    <div className="leading-tight">
      <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className={cls}>{value}</div>
    </div>
  );
}
